/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import React, { Fragment, ReactNode, useCallback, useContext, useEffect, useMemo } from 'react';

import { Select as MuiSelect, SelectProps as MuiSelectProps, MenuItem, FormControl, FormControlProps, FormHelperText, InputLabel, SelectChangeEvent, InputLabelProps, OutlinedInput, Box, Typography, styled, List, ListItem, Divider } from '@mui/material';
/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import { IFormState, IFieldInputProps, useFormErrorBoundary, useFormState } from '@ngt/forms-core';
import SelectIcon from './SelectIcon';
import useErrorStyling, { ErrorClassMapping, ErrorStylingType } from '../../../hooks/form/useErrorStyling';
import { IAdditionalFormInputProps } from './Input';
import { FormError } from '../../../errors';
import FormErrorType from '@/types/form/FormErrorType';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */


export interface IMultiselectItem<TId = any> {
    id?: TId,
    name?: string | ReactNode;
    description?: string;
}

type MultiselectProps<TId = any> = Partial<Omit<MuiSelectProps, 'children' | 'multiple' | 'value'>> & IFieldInputProps<TId[], any, FormError, TId[]> & IAdditionalFormInputProps

export interface IMultiselectProps<TId = any> extends Omit<MultiselectProps<TId>, 'margin'> {
    label?: string;
    FormControlProps?: FormControlProps,
    InputLabelProps?: InputLabelProps;
    options?: IMultiselectItem<TId>[];
    optionsFilter?: (options: IMultiselectItem<TId>[], formState: IFormState<any, FormError>) => IMultiselectItem<TId>[];
    helperText?: ReactNode;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const StyledItem = styled(Box)(({theme}) => ({
    display: 'inline-block'
}))

const StyledSelect = styled(MuiSelect)(({theme}) => ({
    '& .MuiSelect-select': {
        minHeight: '1.4375em'
    }
}))

/*
 * ---------------------------------------------------------------------------------
 * components
 * ---------------------------------------------------------------------------------
 */

const Multiselect = <TId = any>({
    FieldInputProps: { state: { propertyPath, value }, actions: { update, blur, focus, touch } },
    label,
    FormControlProps,
    onChange,
    fullWidth,
    InputLabelProps,
    sx,
    options,
    optionsFilter,
    inputProps: { onFocus: inputPropsFocus, onBlur: inputPropsBlur, ...inputProps } = {},
    hasErrors,
    errors,
    errorType,
    helperText,
    ...rest
}: IMultiselectProps<TId>) => {
    const formState = useFormState(optionsFilter ? undefined : {});

    const filteredOptions = useMemo(() => {
        if (!options) {
            return undefined;
        }

        if (!optionsFilter) {
            return options;
        }

        return optionsFilter(options, formState);
    }, [options, optionsFilter, formState]);

    useEffect(() => {
        if (value && optionsFilter) {
            const filteredValue = value.filter(x => filteredOptions?.find(y => y.id === x));

            if (filteredValue.length !== value.length) {
                update(
                    filteredValue && filteredValue.length > 0 ? filteredValue : undefined, 
                    true, true, true);
            }
        }
    }, [value, optionsFilter, filteredOptions, update]);

    const convertedValue = useMemo(() => {
        return value?.map(x => x?.toString())
    }, [value])

    const onChangeCombined = useCallback((event: SelectChangeEvent<any[]>, child: React.ReactNode) => {

        let values: any[] | undefined = undefined;
        
        if (typeof event.target.value === 'string') {
            values = event.target.value.split(',').map(x => options?.find(y => y.id?.toString() === x?.toString())?.id)
        }
        else {
            values = event.target.value?.map(x => options?.find(y => y.id?.toString() === x?.toString())?.id);
        }
        
        if (onChange) {
            onChange(event, child);
        }

        update(
            values && values.length > 0 ? values : undefined, 
            true, true, true);
    }, [onChange, update, options ]);

    const onFocusCombined = React.useCallback((event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (inputPropsFocus) {
            inputPropsFocus(event);
        }

        focus();
    }, [inputPropsFocus, focus]);

    const onBlurCombined = React.useCallback((event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (inputPropsBlur) {
            inputPropsBlur(event);
        }

        blur();
        touch();
    }, [inputPropsBlur, blur, touch]);

    const errorStyling = useErrorStyling(errorType, ErrorStylingType.Input);

    const fullWidthToUse = fullWidth !== undefined ? 
        fullWidth :
        FormControlProps?.fullWidth !== undefined ?
            FormControlProps?.fullWidth :
            true;

    return (
        <FormControl
            {...FormControlProps}
            error={hasErrors || FormControlProps?.error}
            fullWidth={fullWidthToUse}
            disabled={rest.disabled}
            sx={{...sx, ...errorStyling}}
        >
            <InputLabel {...InputLabelProps} id={`${propertyPath}-label`}>{label}</InputLabel>
            <StyledSelect
                {...rest as any}
                labelId={`${propertyPath}-label`}
                inputProps={{
                    ...inputProps,
                    onBlur: onBlurCombined,
                    onFocus: onFocusCombined
                }}
                input={InputLabelProps?.shrink ? <OutlinedInput notched label={label} disabled={rest.disabled} />  : undefined}
                fullWidth={fullWidthToUse}
                label={label}
                multiple
                value={convertedValue ?? []}
                IconComponent={SelectIcon}
                onChange={onChangeCombined}
                renderValue={() => {
                    return (
                        <List disablePadding>
                            {
                                value?.map((id: TId, index: number) => {

                                    const item = options?.find(x => x.id?.toString() === id?.toString());

                                    if (!item) {
                                        return null
                                    }

                                    return (
                                        <Fragment key={(id as any).toString()}>
                                            <ListItem sx={{ 
                                                paddingLeft: 0, 
                                                paddingRight: 0, 
                                                paddingTop: index === 0 ? 0 : undefined, 
                                                paddingBottom: index === (value.length - 1) ? 0 : undefined 
                                                }}
                                            >
                                                <Box>
                                                    {item.name}
                                                    {item.description && (
                                                        <Typography variant="caption">
                                                            {" "}&ndash; {item.description}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </ListItem>
                                            {index + 1 !== value.length && <Divider />}
                                        </Fragment>
                                    )
                                })
                            }
                        </List>
                    )
                }}
            >
                {
                    filteredOptions?.map(item => {
                        return (
                            <MenuItem
                                key={item.id?.toString()}
                                value={item.id?.toString()}
                            >
                                <StyledItem>
                                    {item.name}
                                    {item.description && (
                                        <Typography variant="caption">
                                            {" "}&ndash; {item.description}
                                        </Typography>
                                    )}
                                </StyledItem>
                            </MenuItem>
                        );
                    })
                }
            </StyledSelect>
            {
                hasErrors && (
                    <FormHelperText>
                        {helperText && (<span className={ErrorClassMapping[FormErrorType.HelperText]}>{helperText}</span>)} 
                        {errors && errors.length > 0 && errors?.map((error, i) => (<span className={ErrorClassMapping[error.type!]} key={i}>{error.message}</span>))}
                    </FormHelperText>
                )
            }
        </FormControl>
    );
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default Multiselect;
